<script setup lang="ts">
import type { ProductReviewFragment } from '#graphql-operations'

const props = defineProps<{
  review: ProductReviewFragment
}>()

const emit = defineEmits<{
  (event: 'upvote', review: ProductReviewFragment): void
  (event: 'open', review: ProductReviewFragment, assetIndex: number): void
}>()

const { t, d } = useI18n()

const productReviewStore = useProductReviewsStore()

const showNavigation = computed(() => (props.review.assets?.length || 0) > 4)
const hasAssets = computed(() => (props.review.assets?.length || 0) > 0)
</script>

<template>
  <UiCard class="border-none p-0 shadow-none">
    <UiCardHeader class="pb-2 !p0">
      <div class="mt4 flex items-center gap-3">
        <UiAvatar>
          <UiAvatarFallback>
            <Icon name="i-heroicons:user-20-solid" class="size-5 text-foreground/70" />
          </UiAvatarFallback>
        </UiAvatar>

        <div class="flex-1">
          <div class="flex items-center justify-between">
            <h3 class="text-foreground font-semibold">
              {{ review.authorName }}
            </h3>
            <time :datetime="review.createdAt" class="text-sm text-muted-foreground">
              {{ d(review.createdAt) }}
            </time>
          </div>

          <div class="mt-1 flex items-center">
            <Rating :rating="review.rating" class="-ml-0.5" />
            <p class="sr-only">
              {{ t('product.rating.x_out_of_y_stars.label', { x: review.rating, y: 5 }) }}
            </p>
          </div>
        </div>
      </div>
    </UiCardHeader>

    <UiCardContent class="!p0">
      <!-- Review Content -->
      <div class="mt-4 space-y-3">
        <div v-if="review.summary" class="text-foreground font-medium">
          {{ review.summary }}
        </div>

        <div v-if="review.body" class="text-muted-foreground">
          {{ review.body }}
        </div>

        <!-- Review Assets -->
        <div v-if="hasAssets" class="mt-4">
          <UiCarousel
            class="relative w-full"
            :opts="{
              dragFree: showNavigation,
              watchDrag: showNavigation,
              slidesToScroll: 4,
              containScroll: 'trimSnaps',
            }"
          >
            <UiCarouselContent class="-ml-4">
              <UiCarouselItem
                v-for="(asset, index) in review.assets"
                :key="index"
                class="basis-1/4 pl-4"
              >
                <div
                  class="relative aspect-square cursor-pointer overflow-hidden border rounded-md bg-background transition-opacity hover:opacity-90"
                  @click="emit('open', review, index)"
                >
                  <!-- Asset type indicator -->
                  <UiButton
                    v-if="isVideo(asset.type)"
                    variant="outline"
                    size="icon"
                    class="absolute left-1/2 top-1/2 z-10 transform rounded-full -translate-x-1/2 -translate-y-1/2"
                  >
                    <Icon name="lucide:play" class="size-6 text-muted-foreground" />
                  </UiButton>

                  <video
                    v-if="isVideo(asset.type)"
                    :poster="asset.preview"
                    class="aspect-square h-full w-full object-cover"
                  >
                    <source :src="asset.source" :type="asset.mimeType">
                  </video>
                  <NuxtImg
                    v-else
                    :src="asset.preview"
                    class="aspect-square h-full w-full object-cover"
                  />
                </div>
              </UiCarouselItem>
            </UiCarouselContent>

            <UiCarouselPrevious
              v-if="showNavigation"
              class="absolute top-1/2 h-8 w-8 bg-white/90 shadow-md -left-2 -translate-y-1/2 hover:bg-white"
            />
            <UiCarouselNext
              v-if="showNavigation"
              class="absolute top-1/2 h-8 w-8 bg-white/90 shadow-md -right-2 -translate-y-1/2 hover:bg-white"
            />
          </UiCarousel>
        </div>
      </div>
    </UiCardContent>

    <UiCardFooter class="pt-0 !p0">
      <div class="mt4 flex items-center justify-between">
        <!-- Helpful button -->
        <UiButton
          variant="outline"
          size="sm"
          class="h-8"
          @click="emit('upvote', review)"
        >
          <Icon
            :name="productReviewStore.hasVoted(review.id) ? 'i-heroicons:hand-thumb-up-16-solid' : 'i-heroicons:hand-thumb-up'"
            class="mr-1.5 size-4"
          />
          {{ t('general.useful') }}
          <span v-if="review.upvotes > 0" class="ml-1 rounded-full bg-secondary/20 px-1.5 py-0.5 text-xs font-medium">
            {{ review.upvotes }}
          </span>
        </UiButton>

        <!-- View Assets button -->
        <UiButton
          v-if="hasAssets"
          variant="ghost"
          size="sm"
          class="h-8"
          @click="emit('open', review, 0)"
        >
          <Icon name="i-heroicons:photo" class="mr-1.5 size-4" />
          {{ t('general.view_photos') }}
        </UiButton>
      </div>
    </UiCardFooter>
  </UiCard>
</template>
